import React, { useEffect, useState } from 'react';
import useApi from '../hooks/useApi';
import Table from '../components/ui/Table';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import Combobox from '../components/ui/Combobox';
import { formatDateTime } from '../utils/helpers';
import { 
  Activity, Shield, User, Clock, Filter, Download, 
  BarChart2, FileText, CheckCircle, AlertTriangle, Eye 
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, AreaChart, Area
} from 'recharts';
import { exportToExcel } from '../utils/exportUtils';
import { fetchAllPaginatedRows } from '../utils/paginatedExport';
import toast from 'react-hot-toast';
import { useUIStore } from '../store/useUIStore';

const StatCard = ({ title, value, icon: Icon, color, bg, description }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 flex items-start justify-between transition-all hover:shadow-md">
    <div>
      <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
      <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
      {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
    </div>
    <div className={`p-3 rounded-lg ${bg} ${color}`}>
      <Icon className="w-6 h-6" />
    </div>
  </div>
);

const DetailView = ({ details }) => {
  if (!details) return <p className="text-gray-500 italic">No details available</p>;

  let parsedDetails = details;
  // Handle potential double-stringification
  while (typeof parsedDetails === 'string') {
    try {
       const next = JSON.parse(parsedDetails);
       if (parsedDetails === next) break;
       parsedDetails = next;
    } catch {
       break;
    }
  }

  if (typeof parsedDetails === 'object' && parsedDetails !== null) {
    return (
      <div className="space-y-3">
        {Object.entries(parsedDetails).map(([key, value]) => (
          <div key={key} className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 border-b border-gray-100 pb-2 last:border-0">
            <span className="text-sm font-medium text-gray-500 capitalize">
              {key.replace(/([A-Z])/g, ' $1').trim()}
            </span>
            <div className="sm:col-span-2 text-sm text-gray-900 break-words font-mono bg-gray-50 p-2 rounded overflow-x-auto">
              {typeof value === 'object' ? (
                <pre className="whitespace-pre-wrap leading-relaxed">{JSON.stringify(value, null, 2)}</pre>
              ) : (
                <span className="whitespace-pre-wrap">{String(value)}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <p className="text-gray-700 whitespace-pre-wrap">{String(parsedDetails)}</p>;
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({
    totalActions: 0,
    actionsToday: 0,
    actionsByType: [],
    activityTrend: [],
    topUsers: []
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    action: '',
    entityType: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const { get } = useApi();
  const { tableDensity, setTableDensity } = useUIStore();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to page 1 on search
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchLogs();
  }, [page, sortConfig, debouncedSearch]); // Trigger only on table params change

  useEffect(() => {
    fetchStats();
  }, []); // Initial stats load

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page,
        limit: 20,
        sortBy: sortConfig.key,
        order: sortConfig.direction,
        search: debouncedSearch,
        ...filters
      });
      
      const response = await get(`/audit?${queryParams.toString()}`);
      setLogs(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch audit logs', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await get('/audit/stats');
      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch audit stats', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setPage(1);
    fetchLogs();
    // Optionally refresh stats with filters if backend supports it
    // fetchStats(); 
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const fetchAllLogsForExport = async () => {
    return fetchAllPaginatedRows({
      fetchPage: (currentPage) => {
      const queryParams = new URLSearchParams({
        page: String(currentPage),
        limit: '200',
        sortBy: sortConfig.key,
        order: sortConfig.direction,
        search: debouncedSearch,
        ...filters
      });

        return get(`/audit?${queryParams.toString()}`);
      }
    });
  };

  const handleExport = async () => {
    const rows = await fetchAllLogsForExport();
    exportToExcel(rows, 'Audit_Log');
    toast.success('Audit log exported to Excel');
  };

  const getActionColor = (action) => {
    if (action.includes('CREATE')) return 'success';
    if (action.includes('UPDATE')) return 'info';
    if (action.includes('DELETE')) return 'error';
    if (action.includes('LOGIN')) return 'primary';
    return 'default';
  };

  const parseDetails = (details) => {
    if (!details) return null;
    let parsed = details;
    while (typeof parsed === 'string') {
      try {
        const next = JSON.parse(parsed);
        if (parsed === next) break;
        parsed = next;
      } catch {
        break;
      }
    }
    return parsed;
  };

  const isEmptyObject = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    return Object.keys(value).length === 0;
  };

  const formatDetailsPreview = (details) => {
    if (!details) return '-';
    try {
      let parsed = details;
      // Handle potential double-stringification
      while (typeof parsed === 'string') {
        try {
           const next = JSON.parse(parsed);
           if (parsed === next) break;
           parsed = next;
        } catch {
           break;
        }
      }

      if (typeof parsed === 'object' && parsed !== null) {
        // Prioritize common fields for summary
        if (parsed.subject && parsed.recipient) return `Subject: ${parsed.subject}, To: ${parsed.recipient}`;
        if (parsed.subject) return `Subject: ${parsed.subject}`;
        if (parsed.name) return `Name: ${parsed.name}`;
        if (parsed.title) return `Title: ${parsed.title}`;
        
        // Fallback: Show first key-value pair
        const firstKey = Object.keys(parsed)[0];
        return firstKey ? `${firstKey}: ${parsed[firstKey]}` : JSON.stringify(parsed);
      }
      return String(parsed);
    } catch (e) {
      return String(details);
    }
  };

  const formatDetailsPreviewRow = (row) => {
    const parsed = parseDetails(row.details);
    if (parsed && typeof parsed === 'object' && !isEmptyObject(parsed)) {
      const summary = parsed.summary && typeof parsed.summary === 'object' ? parsed.summary : null;
      const deleted = parsed.deleted && typeof parsed.deleted === 'object' ? parsed.deleted : null;
      const s = summary || deleted;
      if (s) {
        const number = s.dispatchNumber || s.diaryNumber || s.fileNumber || null;
        const subject = s.subject || null;
        if (number && subject) return `${number} — ${subject}`;
        if (number) return String(number);
        if (subject) return `Subject: ${subject}`;
      }
      return formatDetailsPreview(row.details);
    }

    if (row.action && String(row.action).toUpperCase().includes('DELETE')) {
      const type = row.entityType || 'Record';
      const id = row.entityId ? ` #${row.entityId}` : '';
      return `Deleted ${type}${id}`;
    }

    const type = row.entityType || 'Record';
    const id = row.entityId ? ` #${row.entityId}` : '';
    return `${type}${id}`;
  };

  const buildExpandedDetails = (row) => {
    const parsed = parseDetails(row.details);
    const payload = parsed && typeof parsed === 'object' && !isEmptyObject(parsed) ? parsed : null;
    return {
      meta: {
        timestamp: row.timestamp,
        user: row.userName || row.userEmail || 'Unknown',
        action: row.action || '-',
        entityType: row.entityType || '-',
        entityId: row.entityId || '-',
        ipAddress: row.ipAddress || '-'
      },
      payload: payload || {}
    };
  };

  const columns = [
    { 
      header: 'Timestamp', 
      accessorKey: 'timestamp', 
      sortable: true, 
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{formatDateTime(row.timestamp).split(',')[0]}</span>
          <span className="text-xs text-gray-500">{formatDateTime(row.timestamp).split(',')[1]}</span>
        </div>
      )
    },
    { 
      header: 'User', 
      accessorKey: 'userName', 
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-bold">
            {row.userName ? row.userName.charAt(0).toUpperCase() : 'U'}
          </div>
          <span className="text-sm font-medium text-gray-700">{row.userName || 'Unknown'}</span>
        </div>
      )
    },
    { 
      header: 'Action', 
      accessorKey: 'action', 
      sortable: true,
      render: (row) => <Badge variant={getActionColor(row.action)}>{row.action}</Badge>
    },
    { header: 'Entity', accessorKey: 'entityType', className: 'text-sm text-gray-600' },
    { 
      header: 'Details', 
      accessorKey: 'details', 
      className: 'max-w-md truncate text-sm text-gray-500',
      cellClassName: 'max-w-md truncate text-sm text-gray-500',
      render: (row) => (
        <span title={String(row.details)} className="cursor-help">
          {formatDetailsPreviewRow(row)}
        </span>
      )
    },
    { header: 'IP Address', accessorKey: 'ipAddress', className: 'text-xs text-gray-400 font-mono hidden lg:table-cell', cellClassName: 'text-xs text-gray-400 font-mono hidden lg:table-cell' },
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-8 h-8 text-indigo-600" />
              Audit Trail
            </h1>
            <p className="text-gray-500 mt-1">Track and monitor system security and user activities</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
              <Button
                size="sm"
                variant={tableDensity === 'comfortable' ? 'primary' : 'ghost'}
                onClick={() => setTableDensity('comfortable')}
              >
                Comfortable
              </Button>
              <Button
                size="sm"
                variant={tableDensity === 'compact' ? 'primary' : 'ghost'}
                onClick={() => setTableDensity('compact')}
              >
                Compact
              </Button>
            </div>
             <Button variant="secondary" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button onClick={fetchLogs}>
              <Activity className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <StatCard 
            title="Total Actions" 
            value={stats.totalActions} 
            icon={Activity} 
            color="text-indigo-600" 
            bg="bg-indigo-50"
            description="All time system events"
          />
          <StatCard 
            title="Today's Activity" 
            value={stats.actionsToday} 
            icon={Clock} 
            color="text-emerald-600" 
            bg="bg-emerald-50" 
            description="Events recorded today"
          />
          <StatCard 
            title="Active Users" 
            value={stats.topUsers?.length || 0} 
            icon={User} 
            color="text-blue-600" 
            bg="bg-blue-50"
            description="Users active recently"
          />
          <StatCard 
            title="Security Events" 
            value="0" 
            icon={Shield} 
            color="text-amber-600" 
            bg="bg-amber-50"
            description="Failed logins & warnings"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Activity Trend Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-gray-500" />
              Activity Trend (Last 7 Days)
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.activityTrend}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fill: '#6b7280', fontSize: 12}} />
                  <YAxis axisLine={false} tickLine={false} tick={{fill: '#6b7280', fontSize: 12}} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Area type="monotone" dataKey="count" stroke="#6366f1" fillOpacity={1} fill="url(#colorCount)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Actions Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-500" />
              Top Actions
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.actionsByType} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" hide />
                  <YAxis dataKey="action" type="category" width={100} tick={{fill: '#4b5563', fontSize: 11}} />
                  <Tooltip cursor={{fill: 'transparent'}} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <SearchInput 
                value={search} 
                onChange={setSearch}
                onClear={() => setSearch('')}
                placeholder="Search logs..." 
              />
            </div>
            <div className="w-full lg:w-40">
              <Input 
                type="date" 
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                className="mb-0"
              />
            </div>
            <div className="w-full lg:w-40">
              <Input 
                type="date" 
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                className="mb-0"
              />
            </div>
            <div className="w-full lg:w-48">
              <Combobox
                options={[
                  { value: "", label: "All Actions" },
                  { value: "LOGIN", label: "Login" },
                  { value: "LOGOUT", label: "Logout" },
                  { value: "VIEW_RECEIPT", label: "View Diary" },
                  { value: "VIEW_DISPATCH", label: "View Dispatch" },
                  { value: "CREATE_DISPATCH", label: "Create Dispatch" },
                  { value: "UPDATE_DISPATCH", label: "Update Dispatch" },
                  { value: "CREATE_RECEIPT", label: "Create Diary" },
                  { value: "UPDATE_RECEIPT", label: "Update Diary" },
                  { value: "EFILE_CREATE", label: "e-File: Create" },
                  { value: "EFILE_VIEW_WORKSPACE", label: "e-File: View" },
                  { value: "EFILE_DRAFT_SAVE", label: "e-File: Save Draft" },
                  { value: "EFILE_DRAFT_COMMIT_FORWARD", label: "e-File: Commit Draft & Forward" },
                  { value: "EFILE_NOTE_FORWARD", label: "e-File: Note & Forward" },
                  { value: "EFILE_FORWARD", label: "e-File: Forward" },
                  { value: "EFILE_APPROVE_CLOSE", label: "e-File: Approve & Close" },
                  { value: "EFILE_UPLOAD", label: "e-File: Upload" },
                  { value: "EFILE_SUBJECT_UPDATE", label: "e-File: Update Subject" },
                  { value: "EFILE_DELETE", label: "e-File: Delete" },
                  { value: "EFILE_ARCHIVE", label: "e-File: Archive" }
                ]}
                value={filters.action}
                onChange={(val) => handleFilterChange({ target: { name: 'action', value: val } })}
                className="w-full"
                placeholder="All Actions"
              />
            </div>
            <Button onClick={applyFilters} icon={Filter} variant="secondary">
              Filter
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto">
            <Table 
              columns={columns} 
              data={logs} 
              sortConfig={sortConfig}
              onSort={handleSort}
              className="border-0 shadow-none rounded-none"
              density={tableDensity}
              renderExpandedRow={(row) => (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Timestamp</span>
                      <span className="text-sm text-gray-900">{formatDateTime(row.timestamp)}</span>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">User</span>
                      <span className="text-sm text-gray-900">{row.userName || row.userEmail || 'Unknown'}</span>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Action</span>
                      <Badge variant={getActionColor(row.action)}>{row.action}</Badge>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">IP Address</span>
                      <span className="text-sm font-mono text-gray-600">{row.ipAddress || '-'}</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Payload Details
                    </h4>
                    <div className="bg-white p-3 sm:p-4 rounded border border-gray-200 shadow-sm max-h-[60vh] overflow-y-auto overflow-x-auto">
                      <DetailView details={buildExpandedDetails(row)} />
                    </div>
                  </div>
                </div>
              )}
            />
          </div>
          <Pagination 
            currentPage={page} 
            totalPages={totalPages} 
            onPageChange={setPage} 
            totalItems={logs.length}
            itemsPerPage={20}
          />
        </div>
    </div>
  );
};

export default AuditLog;
